import { useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { createCompany, updateCompany, uploadCompanyImage } from "@/lib/economy-api";
import { parseZlatkaInput, grosToZlatkaDecimalString } from "@/lib/currency";
import { COMPANY_FORM } from "@/constants/testIds";

const ACCEPTED_IMAGE_TYPES = "image/png,image/jpeg,image/webp,image/gif";

// Create/edit dialog for a podnik. Plain <form> + FormData, matching the
// pattern already used by AccountPage.jsx's login form rather than
// introducing react-hook-form (no existing usage of it anywhere in the app).
// The image is the one exception - it uploads immediately on file selection
// (straight to the VPS via /api/economy/uploads/company-image) rather than
// being part of the form's own submit, so the resulting URL is just plain
// state by the time the rest of the form is submitted.
export const CompanyFormDialog = ({ open, onClose, company, onSaved }) => {
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(company?.image_url ?? null);
  const isEdit = Boolean(company);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { image_url } = await uploadCompanyImage(file);
      setImageUrl(image_url);
    } catch (err) {
      toast("Nahrání obrázku se nezdařilo", {
        description: err.response?.data?.detail ?? "Zkus to prosím znovu.",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const data = new FormData(e.target);
    const totalValue = parseZlatkaInput(data.get("total_value"));
    const weeklyProfit = parseZlatkaInput(data.get("weekly_profit"));
    if (!totalValue || !weeklyProfit) {
      toast("Neplatná částka", { description: "Zadej hodnotu a zisk jako nezáporné číslo zlatek." });
      return;
    }

    const npcOwnerName = data.get("npc_owner_name")?.trim();
    if (!npcOwnerName) {
      toast("Chybí NPC vlastník", { description: "Zadej jméno NPC, které podnik ze 100 % vlastní na začátku." });
      return;
    }
    const payload = {
      name: data.get("name").trim(),
      description: data.get("description")?.trim() || null,
      image_url: imageUrl || null,
      total_value: totalValue,
      weekly_profit: weeklyProfit,
      npc_owner_name: npcOwnerName,
      percent_for_sale: Number(data.get("percent_for_sale")),
    };

    setSubmitting(true);
    try {
      if (isEdit) {
        await updateCompany(company.id, payload);
        toast("Podnik upraven", { description: `„${payload.name}“ byl úspěšně uložen.` });
      } else {
        await createCompany(payload);
        toast("Podnik založen", { description: `„${payload.name}“ nyní čeká na přidělení podílů.` });
      }
      onSaved();
    } catch (err) {
      toast("Uložení se nezdařilo", {
        description: err.response?.data?.detail ?? "Zkus to prosím znovu.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-testid={COMPANY_FORM.dialog}
        className="border-white/10 bg-[#17171b] text-white sm:max-w-lg"
      >
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight">
            {isEdit ? "Upravit podnik" : "Nový podnik"}
          </DialogTitle>
          <DialogDescription className="text-white/50">
            Horowitzova městská banka eviduje podíly, ne skutečné zboží - vyplň, co písař
            v knihách potřebuje vědět.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              Název podniku
            </label>
            <input
              name="name"
              defaultValue={company?.name ?? ""}
              required
              maxLength={120}
              data-testid={COMPANY_FORM.nameInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
            />
          </div>

          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              Popis
            </label>
            <textarea
              name="description"
              defaultValue={company?.description ?? ""}
              rows={3}
              data-testid={COMPANY_FORM.descriptionInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
            />
          </div>

          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              Obrázek (volitelné)
            </label>
            {imageUrl && (
              <div className="mb-2 flex items-center gap-3">
                <img
                  src={imageUrl}
                  alt=""
                  data-testid={COMPANY_FORM.imagePreview}
                  className="h-20 w-32 rounded-md border border-white/10 object-cover"
                />
                <button
                  type="button"
                  onClick={() => setImageUrl(null)}
                  data-testid={COMPANY_FORM.imageRemoveButton}
                  className="rounded-md border border-white/20 px-3 py-1.5 text-xs uppercase tracking-wide text-white/70 hover:border-red-400/60 hover:text-red-300"
                >
                  Odstranit obrázek
                </button>
              </div>
            )}
            <input
              type="file"
              accept={ACCEPTED_IMAGE_TYPES}
              onChange={handleFileChange}
              disabled={uploading}
              data-testid={COMPANY_FORM.imageFileInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-sm text-white/70 outline-none file:mr-3 file:rounded file:border-0 file:bg-[#F0801F] file:px-3 file:py-1.5 file:text-xs file:font-600 file:uppercase file:text-[#141417] focus:border-[#F0801F]/60"
            />
            {uploading && <p className="mt-1.5 text-xs text-white/40">Nahrávám na server…</p>}
            <p className="mt-1.5 text-xs text-white/40">JPG, PNG, WebP nebo GIF, max 5 MB. Ukládá se přímo na VPS.</p>
          </div>

          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              NPC vlastník (na začátku vlastní 100 % podniku)
            </label>
            <input
              name="npc_owner_name"
              defaultValue={company?.npc_owner_name ?? ""}
              required
              maxLength={120}
              placeholder="např. Rada starších, Cech kupců…"
              data-testid={COMPANY_FORM.npcOwnerNameInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
            />
          </div>

          <div>
            <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
              Nabídnout k prodeji hráčům (%)
            </label>
            <input
              name="percent_for_sale"
              type="number"
              min="0"
              max="100"
              step="1"
              required
              defaultValue={company?.percent_for_sale ?? 0}
              data-testid={COMPANY_FORM.percentForSaleInput}
              className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
            />
            <p className="mt-1.5 text-xs text-white/40">
              Zbytek zůstává ve vlastnictví NPC, dokud ho GM ručně nepřidělí.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
                Celková hodnota (zlatky)
              </label>
              <input
                name="total_value"
                type="number"
                min="0"
                step="0.001"
                required
                defaultValue={company ? grosToZlatkaDecimalString(company.total_value_gros) : ""}
                data-testid={COMPANY_FORM.totalValueZlatkaInput}
                className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
              />
            </div>
            <div>
              <label className="mb-1.5 block font-display text-[11px] uppercase tracking-[0.16em] text-white/50">
                Týdenní čistý zisk (zlatky)
              </label>
              <input
                name="weekly_profit"
                type="number"
                min="0"
                step="0.001"
                required
                defaultValue={company ? grosToZlatkaDecimalString(company.weekly_profit_base_gros) : ""}
                data-testid={COMPANY_FORM.weeklyProfitZlatkaInput}
                className="w-full rounded-md border border-white/10 bg-white/[0.04] px-4 py-2.5 text-white outline-none focus:border-[#F0801F]/60"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || uploading}
            data-testid={COMPANY_FORM.submitButton}
            className="w-full rounded-md bg-[#F0801F] py-3 font-display text-sm font-600 uppercase tracking-[0.12em] text-[#141417] transition-colors hover:bg-[#ff9438] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Ukládám…" : isEdit ? "Uložit změny" : "Založit podnik"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
